/**
 * Маппинг icon string -> статический компонент (unplugin-icons).
 * Иконки подтягиваются через auto-import — просто добавьте строку в объект ниже.
 *
 * Новая иконка: найдите на icones.js.org формат collection:icon-name,
 * добавьте в ICON_MAP: 'collection:icon-name': IconCollectionIconName
 * (PascalCase: eva:plus-fill -> IconEvaPlusFill). Импорт не нужен.
 *
 * Неизвестные иконки (circle-flags:XX, params из API) → добавляем сюда, в итоге неизвестных иконок быть не должно.
 */
import type { ComponentType } from 'react';

type IconProps = { width?: number; height?: number };

export const ICON_MAP: Record<string, ComponentType<IconProps>> = {
  // columns.ts, statusIcon/sync
  'ant-design:google-outlined': IconAntDesignGoogleOutlined,
  'majesticons:money-line': IconMajesticonsMoneyLine,
  'majesticons:money-minus-line': IconMajesticonsMoneyMinusLine,
  'majesticons:money-plus-line': IconMajesticonsMoneyPlusLine,
  'mdi:package-variant': IconMdiPackageVariant,
  'mdi:package-variant-closed-plus': IconMdiPackageVariantClosedPlus,
  'mdi:package-variant-minus': IconMdiPackageVariantMinus,
  // sites/constants, PrivateFiles, common
  'eva:arrow-ios-downward-fill': IconEvaArrowIosDownwardFill,
  'eva:arrow-ios-back-fill': IconEvaArrowIosBackFill,
  'eva:bell-fill': IconEvaBellFill,
  'eva:arrow-ios-forward-fill': IconEvaArrowIosForwardFill,
  'eva:book-open-outline': IconEvaBookOpenOutline,
  'eva:car-outline': IconEvaCarOutline,
  'eva:checkmark-circle-2-outline': IconEvaCheckmarkCircle2Outline,
  'eva:chevron-down-fill': IconEvaChevronDownFill,
  'eva:cloud-upload-fill': IconEvaCloudUploadFill,
  'eva:close-fill': IconEvaCloseFill,
  'eva:close-circle-outline': IconEvaCloseCircleOutline,
  'eva:credit-card-outline': IconEvaCreditCardOutline,
  'eva:download-fill': IconEvaDownloadFill,
  'eva:edit-2-outline': IconEvaEdit2Outline,
  'eva:edit-2-fill': IconEvaEdit2Fill,
  'eva:eye-fill': IconEvaEyeFill,
  'eva:file-add-fill': IconEvaFileAddFill,
  'eva:file-add-outline': IconEvaFileAddOutline,
  'eva:file-text-outline': IconEvaFileTextOutline,
  'eva:globe-2-outline': IconEvaGlobe2Outline,
  'eva:info-outline': IconEvaInfoOutline,
  'eva:repeat-fill': IconEvaRepeatFill,
  'eva:search-fill': IconEvaSearchFill,
  'eva:settings-2-outline': IconEvaSettings2Outline,
  'eva:trash-2-outline': IconEvaTrash2Outline,
  'eva:clock-outline': IconEvaClockOutline,
  'solar:box-bold': IconSolarBoxBold,
  'solar:cart-bold': IconSolarCartBold,
  'solar:tag-bold': IconSolarTagBold,
  'wpf:view-file': IconWpfViewFile,
  'eva:edit-fill': IconEvaEditFill,
  'eva:radio-button-off-fill': IconEvaRadioButtonOffFill,
  'eva:checkmark-circle-2-fill': IconEvaCheckmarkCircle2Fill,
  'eva:droplet-off-outline': IconEvaDropletOffOutline,
  'eva:droplet-outline': IconEvaDropletOutline,
  'eva:plus-fill': IconEvaPlusFill,
  'eva:plus-circle-fill': IconEvaPlusCircleFill,
  'eva:plus-circle-outline': IconEvaPlusCircleOutline,
  'mdi:plus-box-multiple-outline': IconMdiPlusBoxMultipleOutline,
  'eva:chevron-right-fill': IconEvaChevronRightFill,
  'fluent-mdl2:product-variant': IconFluentMdl2ProductVariant,
  'fluent-mdl2:work-item': IconFluentMdl2WorkItem,
  'mdi:book-open-variant': IconMdiBookOpenVariant,
  'ion:create-outline': IconIonCreateOutline,
  'ion:pencil-outline': IconIonPencilOutline,
  'carbon:document-import': IconCarbonDocumentImport,
  'carbon:delivery': IconCarbonDelivery,
  'carbon:zoom-in': IconCarbonZoomIn,
  'carbon:zoom-out': IconCarbonZoomOut,
  'carbon:play': IconCarbonPlay,
  'carbon:close': IconCarbonClose,
  'carbon:chevron-left': IconCarbonChevronLeft,
  'carbon:chevron-right': IconCarbonChevronRight,
  'carbon:fit-to-screen': IconCarbonFitToScreen,
  'eva:eye-off-fill': IconEvaEyeOffFill,
  'material-symbols:shopping-cart-outline-rounded': IconMaterialSymbolsShoppingCartOutlineRounded,
  'mdi:message-reply-text-outline': IconMdiMessageReplyTextOutline,
  'tabler:building-warehouse': IconTablerBuildingWarehouse,
  'ci:show': IconCiShow,
  'ph:list-dashes-bold': IconPhListDashesBold,
  'tabler:brand-google-analytics': IconTablerBrandGoogleAnalytics,
  'uiw:pay': IconUiwPay,
  'ooui:block': IconOouiBlock,
  'ooui:un-block': IconOouiUnBlock,
  'ic:round-account-box': IconIcRoundAccountBox,
  'ic:round-receipt': IconIcRoundReceipt,
  'ic:round-vpn-key': IconIcRoundVpnKey,
  'ic:round-add-a-photo': IconIcRoundAddAPhoto,
  'ic:round-refresh': IconIcRoundRefresh,
  'iconoir:on-tag': IconIconoirOnTag,
  'iconoir:off-tag': IconIconoirOffTag,
  'iconoir:tools': IconIconoirTools,
  // circle-flags (country codes)
  'circle-flags:ad': IconCircleFlagsAd,
  'circle-flags:ae': IconCircleFlagsAe,
  'circle-flags:af': IconCircleFlagsAf,
  'circle-flags:ag': IconCircleFlagsAg,
  'circle-flags:ai': IconCircleFlagsAi,
  'circle-flags:al': IconCircleFlagsAl,
  'circle-flags:am': IconCircleFlagsAm,
  'circle-flags:ao': IconCircleFlagsAo,
  'circle-flags:aq': IconCircleFlagsAq,
  'circle-flags:ar': IconCircleFlagsAr,
  'circle-flags:as': IconCircleFlagsAs,
  'circle-flags:at': IconCircleFlagsAt,
  'circle-flags:au': IconCircleFlagsAu,
  'circle-flags:aw': IconCircleFlagsAw,
  'circle-flags:ax': IconCircleFlagsAx,
  'circle-flags:az': IconCircleFlagsAz,
  'circle-flags:ba': IconCircleFlagsBa,
  'circle-flags:bb': IconCircleFlagsBb,
  'circle-flags:bd': IconCircleFlagsBd,
  'circle-flags:be': IconCircleFlagsBe,
  'circle-flags:bf': IconCircleFlagsBf,
  'circle-flags:bg': IconCircleFlagsBg,
  'circle-flags:bh': IconCircleFlagsBh,
  'circle-flags:bi': IconCircleFlagsBi,
  'circle-flags:bj': IconCircleFlagsBj,
  'circle-flags:bl': IconCircleFlagsBl,
  'circle-flags:bm': IconCircleFlagsBm,
  'circle-flags:bn': IconCircleFlagsBn,
  'circle-flags:bo': IconCircleFlagsBo,
  'circle-flags:br': IconCircleFlagsBr,
  'circle-flags:bs': IconCircleFlagsBs,
  'circle-flags:bt': IconCircleFlagsBt,
  'circle-flags:bv': IconCircleFlagsBv,
  'circle-flags:bw': IconCircleFlagsBw,
  'circle-flags:by': IconCircleFlagsBy,
  'circle-flags:bz': IconCircleFlagsBz,
  'circle-flags:ca': IconCircleFlagsCa,
  'circle-flags:cc': IconCircleFlagsCc,
  'circle-flags:cd': IconCircleFlagsCd,
  'circle-flags:cf': IconCircleFlagsCf,
  'circle-flags:cg': IconCircleFlagsCg,
  'circle-flags:ch': IconCircleFlagsCh,
  'circle-flags:ci': IconCircleFlagsCi,
  'circle-flags:ck': IconCircleFlagsCk,
  'circle-flags:cl': IconCircleFlagsCl,
  'circle-flags:cm': IconCircleFlagsCm,
  'circle-flags:cn': IconCircleFlagsCn,
  'circle-flags:co': IconCircleFlagsCo,
  'circle-flags:cr': IconCircleFlagsCr,
  'circle-flags:cu': IconCircleFlagsCu,
  'circle-flags:cv': IconCircleFlagsCv,
  'circle-flags:cw': IconCircleFlagsCw,
  'circle-flags:cx': IconCircleFlagsCx,
  'circle-flags:cy': IconCircleFlagsCy,
  'circle-flags:cz': IconCircleFlagsCz,
  'circle-flags:de': IconCircleFlagsDe,
  'circle-flags:dj': IconCircleFlagsDj,
  'circle-flags:dk': IconCircleFlagsDk,
  'circle-flags:dm': IconCircleFlagsDm,
  'circle-flags:do': IconCircleFlagsDo,
  'circle-flags:dz': IconCircleFlagsDz,
  'circle-flags:ec': IconCircleFlagsEc,
  'circle-flags:ee': IconCircleFlagsEe,
  'circle-flags:eg': IconCircleFlagsEg,
  'circle-flags:eh': IconCircleFlagsEh,
  'circle-flags:er': IconCircleFlagsEr,
  'circle-flags:es': IconCircleFlagsEs,
  'circle-flags:et': IconCircleFlagsEt,
  'circle-flags:fi': IconCircleFlagsFi,
  'circle-flags:fj': IconCircleFlagsFj,
  'circle-flags:fk': IconCircleFlagsFk,
  'circle-flags:fm': IconCircleFlagsFm,
  'circle-flags:fo': IconCircleFlagsFo,
  'circle-flags:fr': IconCircleFlagsFr,
  'circle-flags:ga': IconCircleFlagsGa,
  'circle-flags:gb': IconCircleFlagsGb,
  'circle-flags:gd': IconCircleFlagsGd,
  'circle-flags:ge': IconCircleFlagsGe,
  'circle-flags:gf': IconCircleFlagsGf,
  'circle-flags:gg': IconCircleFlagsGg,
  'circle-flags:gh': IconCircleFlagsGh,
  'circle-flags:gi': IconCircleFlagsGi,
  'circle-flags:gl': IconCircleFlagsGl,
  'circle-flags:gm': IconCircleFlagsGm,
  'circle-flags:gn': IconCircleFlagsGn,
  'circle-flags:gp': IconCircleFlagsGp,
  'circle-flags:gq': IconCircleFlagsGq,
  'circle-flags:gr': IconCircleFlagsGr,
  'circle-flags:gs': IconCircleFlagsGs,
  'circle-flags:gt': IconCircleFlagsGt,
  'circle-flags:gu': IconCircleFlagsGu,
  'circle-flags:gw': IconCircleFlagsGw,
  'circle-flags:gy': IconCircleFlagsGy,
  'circle-flags:hk': IconCircleFlagsHk,
  'circle-flags:hm': IconCircleFlagsHm,
  'circle-flags:hn': IconCircleFlagsHn,
  'circle-flags:hr': IconCircleFlagsHr,
  'circle-flags:ht': IconCircleFlagsHt,
  'circle-flags:hu': IconCircleFlagsHu,
  'circle-flags:id': IconCircleFlagsId,
  'circle-flags:ie': IconCircleFlagsIe,
  'circle-flags:il': IconCircleFlagsIl,
  'circle-flags:im': IconCircleFlagsIm,
  'circle-flags:in': IconCircleFlagsIn,
  'circle-flags:io': IconCircleFlagsIo,
  'circle-flags:iq': IconCircleFlagsIq,
  'circle-flags:ir': IconCircleFlagsIr,
  'circle-flags:is': IconCircleFlagsIs,
  'circle-flags:it': IconCircleFlagsIt,
  'circle-flags:je': IconCircleFlagsJe,
  'circle-flags:jm': IconCircleFlagsJm,
  'circle-flags:jo': IconCircleFlagsJo,
  'circle-flags:jp': IconCircleFlagsJp,
  'circle-flags:ke': IconCircleFlagsKe,
  'circle-flags:kg': IconCircleFlagsKg,
  'circle-flags:kh': IconCircleFlagsKh,
  'circle-flags:ki': IconCircleFlagsKi,
  'circle-flags:km': IconCircleFlagsKm,
  'circle-flags:kn': IconCircleFlagsKn,
  'circle-flags:kp': IconCircleFlagsKp,
  'circle-flags:kr': IconCircleFlagsKr,
  'circle-flags:kw': IconCircleFlagsKw,
  'circle-flags:ky': IconCircleFlagsKy,
  'circle-flags:kz': IconCircleFlagsKz,
  'circle-flags:la': IconCircleFlagsLa,
  'circle-flags:lb': IconCircleFlagsLb,
  'circle-flags:lc': IconCircleFlagsLc,
  'circle-flags:li': IconCircleFlagsLi,
  'circle-flags:lk': IconCircleFlagsLk,
  'circle-flags:lr': IconCircleFlagsLr,
  'circle-flags:ls': IconCircleFlagsLs,
  'circle-flags:lt': IconCircleFlagsLt,
  'circle-flags:lu': IconCircleFlagsLu,
  'circle-flags:lv': IconCircleFlagsLv,
  'circle-flags:ly': IconCircleFlagsLy,
  'circle-flags:ma': IconCircleFlagsMa,
  'circle-flags:mc': IconCircleFlagsMc,
  'circle-flags:md': IconCircleFlagsMd,
  'circle-flags:me': IconCircleFlagsMe,
  'circle-flags:mf': IconCircleFlagsMf,
  'circle-flags:mg': IconCircleFlagsMg,
  'circle-flags:mh': IconCircleFlagsMh,
  'circle-flags:mk': IconCircleFlagsMk,
  'circle-flags:ml': IconCircleFlagsMl,
  'circle-flags:mm': IconCircleFlagsMm,
  'circle-flags:mn': IconCircleFlagsMn,
  'circle-flags:mo': IconCircleFlagsMo,
  'circle-flags:mp': IconCircleFlagsMp,
  'circle-flags:mq': IconCircleFlagsMq,
  'circle-flags:mr': IconCircleFlagsMr,
  'circle-flags:ms': IconCircleFlagsMs,
  'circle-flags:mt': IconCircleFlagsMt,
  'circle-flags:mu': IconCircleFlagsMu,
  'circle-flags:mv': IconCircleFlagsMv,
  'circle-flags:mw': IconCircleFlagsMw,
  'circle-flags:mx': IconCircleFlagsMx,
  'circle-flags:my': IconCircleFlagsMy,
  'circle-flags:mz': IconCircleFlagsMz,
  'circle-flags:na': IconCircleFlagsNa,
  'circle-flags:nc': IconCircleFlagsNc,
  'circle-flags:ne': IconCircleFlagsNe,
  'circle-flags:nf': IconCircleFlagsNf,
  'circle-flags:ng': IconCircleFlagsNg,
  'circle-flags:ni': IconCircleFlagsNi,
  'circle-flags:nl': IconCircleFlagsNl,
  'circle-flags:no': IconCircleFlagsNo,
  'circle-flags:np': IconCircleFlagsNp,
  'circle-flags:nr': IconCircleFlagsNr,
  'circle-flags:nu': IconCircleFlagsNu,
  'circle-flags:nz': IconCircleFlagsNz,
  'circle-flags:om': IconCircleFlagsOm,
  'circle-flags:pa': IconCircleFlagsPa,
  'circle-flags:pe': IconCircleFlagsPe,
  'circle-flags:pf': IconCircleFlagsPf,
  'circle-flags:pg': IconCircleFlagsPg,
  'circle-flags:ph': IconCircleFlagsPh,
  'circle-flags:pk': IconCircleFlagsPk,
  'circle-flags:pl': IconCircleFlagsPl,
  'circle-flags:pm': IconCircleFlagsPm,
  'circle-flags:pn': IconCircleFlagsPn,
  'circle-flags:pr': IconCircleFlagsPr,
  'circle-flags:ps': IconCircleFlagsPs,
  'circle-flags:pt': IconCircleFlagsPt,
  'circle-flags:pw': IconCircleFlagsPw,
  'circle-flags:py': IconCircleFlagsPy,
  'circle-flags:qa': IconCircleFlagsQa,
  'circle-flags:re': IconCircleFlagsRe,
  'circle-flags:ro': IconCircleFlagsRo,
  'circle-flags:rs': IconCircleFlagsRs,
  'circle-flags:ru': IconCircleFlagsRu,
  'circle-flags:rw': IconCircleFlagsRw,
  'circle-flags:sa': IconCircleFlagsSa,
  'circle-flags:sb': IconCircleFlagsSb,
  'circle-flags:sc': IconCircleFlagsSc,
  'circle-flags:sd': IconCircleFlagsSd,
  'circle-flags:se': IconCircleFlagsSe,
  'circle-flags:sg': IconCircleFlagsSg,
  'circle-flags:sh': IconCircleFlagsSh,
  'circle-flags:si': IconCircleFlagsSi,
  'circle-flags:sj': IconCircleFlagsSj,
  'circle-flags:sk': IconCircleFlagsSk,
  'circle-flags:sl': IconCircleFlagsSl,
  'circle-flags:sm': IconCircleFlagsSm,
  'circle-flags:sn': IconCircleFlagsSn,
  'circle-flags:so': IconCircleFlagsSo,
  'circle-flags:sr': IconCircleFlagsSr,
  'circle-flags:ss': IconCircleFlagsSs,
  'circle-flags:st': IconCircleFlagsSt,
  'circle-flags:sv': IconCircleFlagsSv,
  'circle-flags:sx': IconCircleFlagsSx,
  'circle-flags:sy': IconCircleFlagsSy,
  'circle-flags:sz': IconCircleFlagsSz,
  'circle-flags:tc': IconCircleFlagsTc,
  'circle-flags:td': IconCircleFlagsTd,
  'circle-flags:tf': IconCircleFlagsTf,
  'circle-flags:tg': IconCircleFlagsTg,
  'circle-flags:th': IconCircleFlagsTh,
  'circle-flags:tj': IconCircleFlagsTj,
  'circle-flags:tk': IconCircleFlagsTk,
  'circle-flags:tl': IconCircleFlagsTl,
  'circle-flags:tm': IconCircleFlagsTm,
  'circle-flags:tn': IconCircleFlagsTn,
  'circle-flags:to': IconCircleFlagsTo,
  'circle-flags:tr': IconCircleFlagsTr,
  'circle-flags:tt': IconCircleFlagsTt,
  'circle-flags:tv': IconCircleFlagsTv,
  'circle-flags:tw': IconCircleFlagsTw,
  'circle-flags:tz': IconCircleFlagsTz,
  'circle-flags:ua': IconCircleFlagsUa,
  'circle-flags:ug': IconCircleFlagsUg,
  'circle-flags:us': IconCircleFlagsUs,
  'circle-flags:uy': IconCircleFlagsUy,
  'circle-flags:uz': IconCircleFlagsUz,
  'circle-flags:va': IconCircleFlagsVa,
  'circle-flags:vc': IconCircleFlagsVc,
  'circle-flags:ve': IconCircleFlagsVe,
  'circle-flags:vg': IconCircleFlagsVg,
  'circle-flags:vi': IconCircleFlagsVi,
  'circle-flags:vn': IconCircleFlagsVn,
  'circle-flags:vu': IconCircleFlagsVu,
  'circle-flags:wf': IconCircleFlagsWf,
  'circle-flags:ws': IconCircleFlagsWs,
  'circle-flags:xk': IconCircleFlagsXk,
  'circle-flags:ye': IconCircleFlagsYe,
  'circle-flags:yt': IconCircleFlagsYt,
  'circle-flags:za': IconCircleFlagsZa,
  'circle-flags:zm': IconCircleFlagsZm,
  'circle-flags:zw': IconCircleFlagsZw,
  'eva:alert-circle-fill': IconEvaAlertCircleFill,
  'eva:info-fill': IconEvaInfoFill,
  'eva:arrow-back-outline': IconEvaArrowBackOutline,
  'eva:arrow-forward-outline': IconEvaArrowForwardOutline,
  'eva:minus-fill': IconEvaMinusFill,
  'eva:more-horizontal-fill': IconEvaMoreHorizontalFill,
  'eva:move-fill': IconEvaMoveFill,
  'eva:alert-triangle-fill': IconEvaAlertTriangleFill,
  'eva:expand-outline': IconEvaExpandOutline,
  'eva:cloud-upload-outline': IconEvaCloudUploadOutline,
  'eva:copy-outline': IconEvaCopyOutline,
  'carbon:delivery-truck': IconCarbonDeliveryTruck,
  'mdi:history': IconMdiHistory,
  'eva:question-mark-circle-outline': IconEvaQuestionMarkCircleOutline,
  'mdi:sync': IconMdiSync,
  'eva:flash-fill': IconEvaFlashFill
};
